/**
 * @fileoverview Document AI service for system-web — uploads a document to the system-api AI
 * recreate endpoint and returns a DocumentLayout; also exposes the Squirrel document agent.
 * @module services/systemDocumentAiService
 *
 * Responsibilities: - Upload a document (PDF/Word/Markdown/text) to /api/ai/recreate-document
 *                     and return a band/flow DocumentLayout + detected merge-field placeholders.
 *                   - Send a natural-language edit request to /api/ai/document-agent and return
 *                     DocumentOp[] for the canvas to apply.
 * Dependencies:     - apiClient (apiPostFormData / apiFetch) — attaches Authorization: Bearer token.
 * Architecture:     - Approved inherently-online exception (file upload + parse). No Dexie caching
 *                     — the result is handed directly to the builder for in-memory editing. The
 *                     builder persists documentLayout via the local-first save handler.
 *                   - No X-Tenant-Slug header: system-web is tenant-agnostic.
 * Notes:            - Parsing is residency-safe on the server (local by default; LlamaParse
 *                     opt-in). The file itself is never stored; only the derived layout is returned.
 */

import { apiPostFormData, apiFetch } from '../apiClient';

/**
 * @contract DocumentRecreateResult
 * Purpose: Server response from the AI recreate endpoint.
 * Fields:  layout (DocumentLayout JSON), detectedPlaceholders[], parser, tokenCount, source.
 * Rules:   layout is consumed directly by the canvas builder.
 * Sync:    Transient — not persisted until the user saves the template via the builder save handler.
 */
export interface DocumentRecreateResult {
  layout: Record<string, unknown>;
  detectedPlaceholders: string[];
  parser: 'llamaparse' | 'local';
  tokenCount: { input: number; output: number };
  /** Source-file identity for provenance (the file itself is never stored). */
  source: { fileName: string; sizeBytes: number; sha256: string };
}

/**
 * @process recreateDocumentFromUpload
 * Steps:   POST the file as multipart/form-data to /api/ai/recreate-document; return the layout.
 * Inputs:  a File (pdf/docx/doc/md/markdown/txt).
 * Outputs: DocumentRecreateResult.
 * Failure Handling: throws with the server error message on non-2xx.
 * Idempotency: NOT REQUIRED (AI call).
 */
export async function recreateDocumentFromUpload(file: File): Promise<DocumentRecreateResult> {
  const formData = new FormData();
  formData.append('file', file);
  return apiPostFormData<DocumentRecreateResult>('/api/ai/recreate-document', formData);
}

/**
 * @contract DocumentAgentResponse
 * Purpose: Squirrel document-agent reply.
 * Fields:  operations[] (applied to the canvas DocumentLayout), message (assistant reply).
 * Rules:   operations are applied via @firesquirrel/document-render applyDocumentOps.
 * Sync:    Transient — ops applied in-memory; persisted when template is saved.
 */
export interface DocumentAgentResponse {
  operations: unknown[];
  message: string;
  tokenCount?: { input: number; output: number };
}

/**
 * @process askDocumentAgent
 * Steps:   POST the user's request + current layout to /api/ai/document-agent; return ops + message.
 * Inputs:  message + the current DocumentLayout (any-shaped JSON).
 * Outputs: DocumentAgentResponse.
 * Failure Handling: throws with the server error message on non-2xx.
 * Idempotency: NOT REQUIRED (AI call).
 */
export async function askDocumentAgent(message: string, layout: unknown): Promise<DocumentAgentResponse> {
  return apiFetch('/api/ai/document-agent', {
    method: 'POST',
    body: JSON.stringify({ message, layout }),
  });
}
